import React, { FormEventHandler, useCallback, useMemo, useState } from 'react';
import { Filter, InventoryStockStatus, InventoryVariantStatus } from '../../../@types/inventory';
import { IInventory, IPaginationMeta } from '../../../@types';
import { useNavigate } from 'react-router-dom';
import ListTable from './ListTable';
import { AddSquareIcon, DownloadIcon, FilterIcon } from '../../../assets/icons';
import { DropDown } from '../../common';
import InventoryFilter from '../InventoryFilter';
import BinIcon from '../../../assets/icons/BinIcon';
import { Link } from 'react-router-dom';

import { InventoryModal } from '../InventoryModal';
import { createPortal } from 'react-dom';
import Restock from '../restock/Restock';

interface inventoryTableProps {
  inventory: IInventory[];
  isLoading: boolean;
  fetchMoreData: () => void;
  handleDeleteInventory: (id: string) => void;
  meta: IPaginationMeta;
  onSearchData?: (text: string) => void;
  handleSort?: (text: string) => void;
  handleSearch?: (text: string) => void;
  showHead?: boolean;
  handleFilter?: (filter: Filter) => void;
  newLink?: string;
  title?: string;
  error?: boolean;
  isSearching?: boolean;
  headings?: {
    [key: string]: string;
  };
  data?: any[];
  searchResult: any[];
  selectedFilter?: Filter | undefined;
}

const modalEl = document.getElementById('modal')!;

const InventoryListTable = ({
  inventory,
  selectedFilter,
  isLoading,
  fetchMoreData,
  handleDeleteInventory,
  meta,
  handleSort,
  handleSearch,
  searchResult,
  onSearchData,
  showHead = true,
  handleFilter,
}: inventoryTableProps) => {
  const navigate = useNavigate();
  const [dropdown, setDropdown] = React.useState(false);
  const [showFilter, setShowFilter] = React.useState(false);
  const [, setSelectedInventory] = React.useState<IInventory | null>(null);
  const [stockStatus, setStockStatus] = React.useState<string>('');
  const [variantStatus, setVariantStatus] = React.useState<string | ''>('');
  const [openModal, setOpenModal] = useState(false);
  const [id, setId] = useState('')

  const handleFilterIconClick = () => {
    setShowFilter(true);
  };

  const handleCloseModal = () => {
    setShowFilter(false);
  };

  const handleCreatedAtClick = (inv: IInventory) => {
    setSelectedInventory(inv);
  };

  const onSearch = (text: string) => {
    if (handleSearch && onSearchData) handleSearch(text);
  };

  const observer = React.useRef<IntersectionObserver | null>(null);
  const lastInventoryRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && meta.hasMoreData) {
          fetchMoreData();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, meta],
  );

  const memoizedSelectedFilter = useMemo(
    () => selectedFilter,
    [
      selectedFilter && 'stockStatus' in selectedFilter ? selectedFilter.stockStatus : null,
      selectedFilter && 'variantStatus' in selectedFilter ? selectedFilter.variantStatus : null,
    ],
  );

  console.log(memoizedSelectedFilter);

  const handleSearchSelect = (item: any) => {
    navigate(`/inventories/${item.id}/view`);
  };

  const shortenInventoryId = (id: string) => id.substring(0, 6);

  const renderInventoryTableHead = useMemo(
    () => ({
      name: { title: 'Product' },
      variation: { title: 'Variations' },
      price: { title: 'Unit Price' },
      quantityLeft: { title: 'Qty Left' },
      action: { title: 'Action ' },
      bin: { icon: <BinIcon /> },
    }),
    [],
  );

  const renderInventoryData = (inventory: IInventory[]) => {
    return inventory.map((inventory: IInventory) => {
      return {
        id: inventory.id,
        name: (
          <a
            href={`/inventories/${inventory.id}/view`}
            className="text-violet-800 hover:cursor-pointer"
            onClick={() => handleCreatedAtClick(inventory)}
          >
            {inventory.name}
          </a>
        ),
        variations: Array.isArray(inventory.variations) ? inventory.variations.length : 0,
        price: inventory.rate,
        quantityLeft: !inventory.quantity ? 0 : inventory.quantity,
        action: (
          <button onClick={() => handleModal(inventory.id)} className="text-[#044E97] border py-2 px-2.5 rounded-lg">
            Restock
          </button>
        ),

        bin: (
          <div onClick={() => handleDeleteInventory(inventory.id)}>
            <BinIcon color="red" />
          </div>
        ),
      };
    });
  };

  const handleVariantStatusChange = (variantStatus: string) => {
    setVariantStatus(variantStatus.toLowerCase() as InventoryVariantStatus);
  };

  const handleStockStatusChange = (newStockStatus: string) => {
    setStockStatus(newStockStatus.toLowerCase() as InventoryStockStatus);
  };

  const onInventoryFilterSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (handleFilter) {
      handleFilter({ stockStatus, variantStatus });
    }
    setShowFilter(false);
  };
  const fetchPageData = (direction: 'up' | 'down') => {
    if (direction === 'down' && meta.hasMoreData) {
      console.log('down');
      fetchMoreData();
    }
  };
  
  const handleModal = (id:string) => {
    setOpenModal(true);
    setId(id)
  }

  return (
    <>
      <div className="hidden md:block whitespace-nowrap">
        <ListTable
          buttons={[
            {
              text: 'Add Inventory',
              href: '/inventories/create',
              icon: <AddSquareIcon />,
            },
            {
              text: 'Import',
              icon: <DownloadIcon />,
            },
            {
              text: '',
              icon: (
                <DropDown
                  onChange={handleFilterIconClick}
                  show={showFilter}
                  component={
                    <div className="flex ">
                      {' '}
                      <span>Filter</span> <FilterIcon color="#044E97" />{' '}
                    </div>
                  }
                >
                  <InventoryFilter
                    variantStatus={
                      memoizedSelectedFilter && 'variantStatus' in memoizedSelectedFilter
                        ? memoizedSelectedFilter.variantStatus
                        : undefined
                    }
                    setStockStatus={handleStockStatusChange}
                    setVariantStatus={handleVariantStatusChange}
                    stockStatus={
                      memoizedSelectedFilter && 'stockStatus' in memoizedSelectedFilter
                        ? memoizedSelectedFilter.stockStatus
                        : undefined
                    }
                    onSubmit={onInventoryFilterSubmit}
                    onClose={handleCloseModal}
                    showFilter
                  />
                </DropDown>
              ),
            },
          ]}
          head={renderInventoryTableHead}
          searchPlaceHolder="Search"
          selectedFilter={selectedFilter}
          handleFilter={handleFilter}
          searchDisplayKey="name"
          searchResult={searchResult}
          data={renderInventoryData(inventory)}
          isLoading={isLoading}
          showHead={showHead || undefined}
          handleSearchSelect={handleSearchSelect}
          showPagination
          handleSearch={onSearch}
          handleSort={handleSort}
          fetchMoreData={fetchPageData}
          meta={meta}
          memoizedSelectedFilter={memoizedSelectedFilter}
          handleStockStatusChange={handleStockStatusChange}
          handleVariantStatusChange={handleVariantStatusChange}
          onSubmit={onInventoryFilterSubmit}
          onClose={handleCloseModal}
          inventory={inventory}
          showFilter
          handleDeleteInventory={handleDeleteInventory}
        />
      </div>

      <div className="md:hidden">
        <ListTable
          buttons={[
            {
              text: '',
              icon: (
                <DropDown
                  onChange={handleFilterIconClick}
                  show={showFilter}
                  component={
                    <div className="flex ">
                      {' '}
                      <span>Filter</span> <FilterIcon color="#044E97" />{' '}
                    </div>
                  }
                >
                  <InventoryFilter
                    variantStatus={
                      memoizedSelectedFilter && 'variantStatus' in memoizedSelectedFilter
                        ? memoizedSelectedFilter.variantStatus
                        : undefined
                    }
                    setStockStatus={handleStockStatusChange}
                    setVariantStatus={handleVariantStatusChange}
                    stockStatus={
                      memoizedSelectedFilter && 'stockStatus' in memoizedSelectedFilter
                        ? memoizedSelectedFilter.stockStatus
                        : undefined
                    }
                    onSubmit={onInventoryFilterSubmit}
                    onClose={handleCloseModal}
                    showFilter
                  />
                </DropDown>
              ),
            },
          ]}
          head={renderInventoryTableHead}
          searchPlaceHolder="Search"
          selectedFilter={selectedFilter}
          handleFilter={handleFilter}
          searchDisplayKey="name"
          searchResult={searchResult}
          data={renderInventoryData(inventory)}
          isLoading={isLoading}
          showHead={showHead || undefined}
          handleSearchSelect={handleSearchSelect}
          hideFloatingScrollButton={true}
          showPagination
          handleSearch={onSearch}
          handleSort={handleSort}
          fetchMoreData={fetchPageData}
          meta={meta}
          handleStockStatusChange={handleStockStatusChange}
          handleVariantStatusChange={handleVariantStatusChange}
          onSubmit={onInventoryFilterSubmit}
          onClose={handleCloseModal}
          showFilter
        />
      </div>
      {/* {showModal && createPortal(
        <ModalContent onClose={() => setShowModal(false)} />,
        document.body
      )} */}

      {openModal &&
        createPortal(
          <InventoryModal
            children={<Restock restockId={id} setOpenModal={setOpenModal} />}
            setOpenModal={setOpenModal}
          />,
          document.body,
        )}
    </>
  );
};

export default InventoryListTable;
